import { ALL_ROLES_FLAG } from '../firewall-rule-form/FirewallRuleForm.queries.ts';
import type { FirewallRuleFieldValue } from '../types.ts';

/**
 * Returns a new array of sorted firewall rules
 * The goal is to group by protocol and put the most general rules at the top of the list,
 * first by port range width, then considering allowed roles.  Allowed roles are considered more specific
 * than port ranges.
 */
export function sortFirewallRules(rules: FirewallRuleFieldValue[]) {
  return rules.toSorted(compRules);
}

/**
 * Comparison function to sort firewall rules
 */
function compRules(a: FirewallRuleFieldValue, b: FirewallRuleFieldValue): number {
  // Sort by protocol, alphabetically
  if (a.protocol !== b.protocol) {
    // We want "ANY" on top, but don't need to special-case it because alphabetically it's also first
    return a.protocol < b.protocol ? -1 : 1;
  }

  // -- Protocol is the same --

  // Look at allowed roles
  if (a.allowedRoleID === ALL_ROLES_FLAG && b.allowedRoleID !== ALL_ROLES_FLAG) return -1;
  if (b.allowedRoleID === ALL_ROLES_FLAG && a.allowedRoleID !== ALL_ROLES_FLAG) return 1;

  // -- Neither or both have allowed role --

  // Wider port ranges before smaller ranges
  const aPortRangeCount = a.portRange ? a.portRange.to - a.portRange.from : Number.POSITIVE_INFINITY;
  const bPortRangeCount = b.portRange ? b.portRange.to - b.portRange.from : Number.POSITIVE_INFINITY;

  if (aPortRangeCount !== bPortRangeCount) {
    return bPortRangeCount - aPortRangeCount;
  }

  // -- Same port range --

  // Check tags (more tags before fewer tags, but zero tags on top of all)
  if (a.tags.length !== b.tags.length) {
    if (!a.tags.length) return -1;
    if (!b.tags.length) return 1;
    return b.tags.length - a.tags.length;
  }

  // At this point, we don't care about the order
  return 0;
}
